import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddInvestment from "../../Masters/Scope3/Investment/AddInvestment";

const EXCEL_INVESTMENT_TYPE_KEY = "Investment Type";

const getEnteredInvestmentType = (row) =>
  row?.investmentType ||
  row?.investmentTYPE ||
  row?.["Investment Type"] ||
  row?.["investmentType"] ||
  "";

const getMatchedFlag = (row) => {
  if (!row) return false;
  if (typeof row.investmentMatched === "boolean") return row.investmentMatched;
  if (typeof row.investmentTypeMatched === "boolean")
    return row.investmentTypeMatched;
  if (typeof row.matched === "boolean") return row.matched;
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  return matchedKey ? Boolean(row[matchedKey]) : false;
};

const setMatchedFlag = (row, next) => {
  if (!row) return row;
  if (typeof row.investmentMatched === "boolean")
    return { ...row, investmentMatched: next };
  if (typeof row.investmentTypeMatched === "boolean")
    return { ...row, investmentTypeMatched: next };
  if (typeof row.matched === "boolean") return { ...row, matched: next };
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  if (matchedKey) return { ...row, [matchedKey]: next };
  return { ...row, matched: next };
};

const BulkUploadReview = ({
  matchData = [],
  setMatchData,
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  bulkUploadUrl,
}) => {
  const [masterInvestments, setMasterInvestments] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [addMasterOpen, setAddMasterOpen] = useState(false);
  const [addMasterElement, setAddMasterElement] = useState(null);
  const [pendingCreatedType, setPendingCreatedType] = useState("");
  const [addRowIndex, setAddRowIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  const masterFormInputData = useMemo(
    () => [
      { fieldName: "investmentType", title: "Investment Type", placeholder: "Tap to select" },
      { fieldName: "investeeProjectName", title: "Investee/Project Name", placeholder: "Enter project name" },
      { fieldName: "sector", title: "Sector(s) the investee company operates in", placeholder: "Tap to select" },
      { fieldName: "countryOfOperation", title: "Country of operation", placeholder: "Tap to select" },
      { fieldName: "equityHoldingPercent", title: "Equity Holding (%)", placeholder: "Enter equity holding" },
    ],
    []
  );

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) setFinalJson(formDataBulkUpload);
  }, [formDataBulkUpload]);

  useEffect(() => {
    const fetchMasters = async () => {
      setLoadingMasters(true);
      try {
        const queryParams = new URLSearchParams({
          limit: "1000",
          page: "1",
        }).toString();
        const res = await get(`/master/investmentMasters?${queryParams}`);
        setMasterInvestments(res?.data?.data || []);
      } catch (e) {
        setMasterInvestments([]);
      } finally {
        setLoadingMasters(false);
      }
    };
    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable]);

  const investmentTypeOptions = useMemo(() => {
    const types = (masterInvestments || [])
      .map((f) => f?.investmentType)
      .filter((v) => typeof v === "string" && v.trim().length > 0);
    return Array.from(new Set(types)).sort((a, b) => a.localeCompare(b));
  }, [masterInvestments]);

  useEffect(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return;
    if (!Array.isArray(investmentTypeOptions) || investmentTypeOptions.length === 0)
      return;

    const normalizedOptions = new Map(
      investmentTypeOptions.map((opt) => [String(opt).trim().toLowerCase(), opt])
    );

    let rowChanged = false;
    const nextMatchData = matchData.map((row, index) => {
      const alreadyMatched = getMatchedFlag(row);
      if (alreadyMatched) return row;

      const enteredRaw = getEnteredInvestmentType(row);
      const enteredNormalized = String(enteredRaw || "").trim().toLowerCase();
      if (!enteredNormalized) return row;

      const canonical = normalizedOptions.get(enteredNormalized);
      if (!canonical) return row;

      const selected = getSelectedTypeForIndex(index);
      if (!selected || selected === "add_new") {
        setSelectedTypeForIndex(index, canonical);
      }
      rowChanged = true;
      return setMatchedFlag(row, true);
    });

    if (rowChanged) setMatchData(nextMatchData);
  }, [matchData, investmentTypeOptions]);

  const getSelectedTypeForIndex = (index) => {
    const row = formDataBulkUpload?.[index];
    const selected =
      row?.[EXCEL_INVESTMENT_TYPE_KEY] || row?.investmentType || "";
    return String(selected || "");
  };

  const setSelectedTypeForIndex = (index, nextValue) => {
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      arr[index] = { ...existing, [EXCEL_INVESTMENT_TYPE_KEY]: nextValue };
      return arr;
    });
  };

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      const matched = getMatchedFlag(row);
      if (matched) return false;
      const selected = getSelectedTypeForIndex(idx);
      return !selected || selected === "add_new";
    });
  }, [matchData, formDataBulkUpload]);

  const openAddMasterDrawer = (index) => {
    const entered = String(getEnteredInvestmentType(matchData?.[index]) || "").trim();
    setAddRowIndex(index);
    setPendingCreatedType(entered);
    setAddMasterElement({
      investmentType: entered,
      investeeProjectName: "",
      sector: "",
      countryOfOperation: "",
      equityHoldingPercent: "",
    });
    setAddMasterOpen(true);
    setExpandedRows((prev) => ({ ...prev, [index]: true }));
  };

  const refreshMasterDataAndMarkMatched = async () => {
    try {
      const queryParams = new URLSearchParams({
        limit: "1000",
        page: "1",
      }).toString();
      const res = await get(`/master/investmentMasters?${queryParams}`);
      const list = res?.data?.data || [];
      setMasterInvestments(list);
      if (typeof addRowIndex === "number" && pendingCreatedType) {
        setSelectedTypeForIndex(addRowIndex, pendingCreatedType);
        setMatchData((prev) =>
          (prev || []).map((r, i) =>
            i === addRowIndex ? setMatchedFlag(r, true) : r
          )
        );
      }
    } finally {
      setExpandedRows((prev) => ({
        ...prev,
        ...(addRowIndex !== null ? { [addRowIndex]: false } : {}),
      }));
      setAddRowIndex(null);
      setPendingCreatedType("");
      setAddMasterOpen(false);
      setAddMasterElement(null);
    }
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) =>
        Array.isArray(prev)
          ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
          : prev
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({ ...entry, "Document (optional)": files }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry) => ({ ...entry, "Document (optional)": files }))
      );
    }
  };

  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry) => ({ ...entry, "Document (optional)": null }))
      );
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
            i === index ? { ...entry, "Document (optional)": files } : entry
          )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": files } : entry
        )
      );
    }
  };

  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
            i === index ? { ...entry, "Document (optional)": null } : entry
          )
        : prev
    );
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      const dataToUse =
        Array.isArray(finalJson) && finalJson.length > 0
          ? finalJson
          : formDataBulkUpload;
      const uploadData = {
        excelData: dataToUse,
        documentUploadType,
        commonDocument,
        individualDocuments,
      };
      const res = await post(bulkUploadUrl, uploadData);
      setsnackOpen?.({
        open: true,
        message: res?.data?.message || "Uploaded successfully",
        severity: "success",
      });
      onConfirm?.();
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details
        ?.map((el) => el?.error || el?.reason)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message:
          errMessage || e?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review unmatched investment types and map them with master investment types.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Data Entered</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Master Data</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Action</TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Document</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Investment Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Investment Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} />
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold" }} />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchData || []).map((row, index) => {
                  const entered = getEnteredInvestmentType(row);
                  const matched = getMatchedFlag(row);
                  return (
                    <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>{entered || "-"}</TableCell>
                      <TableCell>
                        {matched ? (
                          <Chip label="Matched" color="success" size="small" />
                        ) : (
                          <FormControl size="small" sx={{ minWidth: "14rem" }}>
                            <Select
                              value={getSelectedTypeForIndex(index) || ""}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                if (nextValue === "add_new") {
                                  setSelectedTypeForIndex(index, "add_new");
                                  openAddMasterDrawer(index);
                                  return;
                                }
                                setSelectedTypeForIndex(index, nextValue);
                                setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                setAddMasterOpen(false);
                                setMatchData((prev) =>
                                  (prev || []).map((r, i) =>
                                    i === index ? setMatchedFlag(r, Boolean(nextValue)) : r
                                  )
                                );
                              }}
                              fullWidth
                              displayEmpty
                              disabled={loadingMasters}
                            >
                              <MenuItem value="">Tap to Select</MenuItem>
                              <MenuItem value="add_new">Add New Investment Type +</MenuItem>
                              {investmentTypeOptions.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {!matched &&
                          (expandedRows[index] ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                setAddMasterOpen(false);
                              }}
                              sx={{ minWidth: "80px" }}
                            >
                              Close
                            </Button>
                          ) : (
                            <Chip
                              label={getSelectedTypeForIndex(index) ? "Selected" : "Unmatched"}
                              color={getSelectedTypeForIndex(index) ? "info" : "warning"}
                              size="small"
                            />
                          ))}
                      </TableCell>
                      {documentUploadConfirmed && documentUploadType === "individual" && (
                        <TableCell>
                          <BulkFileUpload
                            handleChange={handleIndividualDocumentChange}
                            handleClear={handleIndividualDocumentClear}
                            filesList={
                              individualDocuments[index] ||
                              formDataBulkUpload?.[index]?.["Document (optional)"] ||
                              finalJson?.[index]?.["Document (optional)"] ||
                              []
                            }
                            index={index}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={documentUploadConfirmed && documentUploadType === "individual" ? 4 : 3}
                      >
                        <Collapse
                          in={Boolean(expandedRows[index]) && addRowIndex === index}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ mt: 2 }}>
                            <AddInvestment
                              id=""
                              addData={addMasterOpen && addRowIndex === index}
                              setAddData={(next) => {
                                setAddMasterOpen(next);
                                if (!next) {
                                  setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                }
                              }}
                              data={masterInvestments}
                              element={addMasterElement}
                              addUrl="/master/investmentMasters"
                              updateUrl="/master/investmentMasters"
                              fetchData={refreshMasterDataAndMarkMatched}
                              setsnackOpen={setsnackOpen}
                              formInputData={masterFormInputData}
                              embedded
                              embeddedTitle="Add Investment"
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <Stack spacing={2} sx={{ width: "100%" }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Do you want to upload document?</FormLabel>
                <RadioGroup
                  value={documentUploadConfirmed ? "yes" : "no"}
                  onChange={(e) => handleDocumentUploadConfirmation(e.target.value === "yes")}
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
              {documentUploadConfirmed && (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Document Upload Type</FormLabel>
                  <RadioGroup value={documentUploadType} onChange={handleDocumentUploadTypeChange} row>
                    <FormControlLabel value="same" control={<Radio />} label="Same File for All Records" />
                    <FormControlLabel value="individual" control={<Radio />} label="Individual File for Each Record" />
                  </RadioGroup>
                </FormControl>
              )}
              {documentUploadConfirmed && documentUploadType === "same" && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload Common Document:
                </Typography>
                <BulkFileUpload
                  handleChange={handleCommonDocumentChange}
                  handleClear={handleCommonDocumentClear}
                  filesList={commonDocument}
                />
              </Box>
              )}
            </Stack>
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffebee", borderRadius: 1, border: "1px solid #f44336" }}>
              <Typography variant="h6" color="error" sx={{ mb: 1, fontWeight: "bold" }}>
                Error:
              </Typography>
              {uploadValidationErrors.map((error, index) => (
                <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                  - {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={onClose} variant="outlined" color="primary" sx={{ marginRight: "1rem" }} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmUpload} variant="contained" color="primary" disabled={disableConfirm || uploading}>
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showNoDocumentDialog} onClose={() => setShowNoDocumentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>No Documents Uploaded</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have selected to upload documents but haven't uploaded any files.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={() => setShowNoDocumentDialog(false)} variant="outlined" color="primary" sx={{ marginRight: "1rem" }}>
            Upload Documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed Without Documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;
